import React from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body, Heading, Numeric } from '@swyftx/aviary/atoms/Typography';
import { ReviewItem } from '@swyftx/aviary/molecules/ReviewItem';
import { MarginOrderDirection } from '@swyftx/margin/build/types';

import { DATA_FIELDS, SYMBOL } from './ClosingPositionsContentSample.data';

type CloseType = 'market' | 'takeProfit' | 'stopLoss';

type EntryDirection = 'closeAboveEntry' | 'closeBelowEntry';

type Props = {
  direction: MarginOrderDirection;
  type: CloseType;
  entryDirection: EntryDirection;
};

const ClosingPositionsContentSampleEntry: React.FC<Props> = ({ direction, type, entryDirection }) => {
  const { t } = useTranslation('margin');

  const data = DATA_FIELDS[direction][type];
  const isLong = direction === MarginOrderDirection.Long;

  const isAbove = entryDirection === 'closeAboveEntry';

  const profitLossValue = isAbove
    ? isLong
      ? data.closingProfit
      : data.closingLoss
    : isLong
    ? data.closingLoss
    : data.closingProfit;

  const profitLossColor = isAbove === isLong ? 'success' : 'error';

  const profitLossTitle = t(
    `understandingOrderTypes.close.labels.${isAbove === isLong ? 'closingProfit' : 'closingLoss'}`,
  );

  if (
    !t(`understandingOrderTypes.close.${direction}.${type}.${entryDirection}.title`) &&
    !t(`understandingOrderTypes.close.${direction}.${type}.${entryDirection}.content`)
  ) {
    return null;
  }

  return (
    <FlexLayout direction='column' spacing={12}>
      <Heading size='h5'>{t(`understandingOrderTypes.close.${direction}.${type}.${entryDirection}.title`)}</Heading>
      <Body color='secondary'>
        {t(`understandingOrderTypes.close.${direction}.${type}.${entryDirection}.content`, {
          symbol: SYMBOL,
          type: t(`understandingOrderTypes.close.labels.closeType.${type}`).toLowerCase(),
          direction: t(`tradeSide.${direction}`).toLowerCase(),
          averageFillPriceCloseAbove: data.averageFillPriceCloseAbove,
          averageFillPriceCloseBelow: data.averageFillPriceCloseBelow,
        })}
      </Body>
      <FlexLayout direction='column' spacing={4}>
        <ReviewItem
          title={t('understandingOrderTypes.close.labels.averageFillPriceClose')}
          className='mt-24 w-full sm:max-w-[343px] md:w-3/4'
        >
          <Numeric weight='emphasis' color='primary'>
            {entryDirection === 'closeAboveEntry' ? data.averageFillPriceCloseAbove : data.averageFillPriceCloseBelow}
          </Numeric>
        </ReviewItem>
        {profitLossValue && (
          <ReviewItem title={profitLossTitle} className='w-full sm:max-w-[343px] md:w-3/4'>
            <Numeric weight='emphasis' color={profitLossColor}>
              {profitLossValue}
            </Numeric>
          </ReviewItem>
        )}
      </FlexLayout>
    </FlexLayout>
  );
};

export { ClosingPositionsContentSampleEntry };
